import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { color, elevation, fontSize, radius } from '../theme/tokens.js';

/**
 * The sign-in page (FR-001, FR-005, Constitution Principle I, gate 1).
 *
 * The blueprint has no login screen, so this is composed **from blueprint primitives** — the
 * same background, the same brand block and gradient, the same card treatment, the same type
 * scale. Introducing a second visual language here would be a Principle I violation even
 * though nothing in the blueprint shows what this page should look like.
 *
 * Google SSO is the only mechanism (FR-001). There is no password field, no "forgot password",
 * and no alternate path — not because they are hidden, but because they do not exist anywhere
 * in the model.
 *
 * The refusal messages come from the callback's redirect, which carries an error code rather
 * than free text so the server never phrases a message the client has to trust.
 *
 * `ACCESS_PENDING` is the one that is not a failure: since 2026-10-06 every first sign-in
 * lands here until an admin approves it, so it is the first thing most people ever see of the
 * product. It is drawn as a notice rather than an error, thanks them, says plainly what happens
 * next — and does not claim an admin has been told, because nothing tells them.
 */

interface Message {
  title: string;
  detail: string;
  /** `notice` for an expected step, `error` for a refusal. Changes the colour, not the role. */
  tone: 'notice' | 'error';
}

const MESSAGES: Record<string, Message> = {
  ACCESS_PENDING: {
    title: 'You don’t have access to BoB Manager yet',
    detail:
      'Thanks for signing in — your account has been registered and is awaiting approval. Please ask a BoB Manager administrator to grant you access. Once they have, simply sign in again.',
    tone: 'notice',
  },
  DOMAIN_NOT_ALLOWED: {
    title: 'That account cannot use BoB Manager',
    detail:
      'Access is limited to verified company accounts. No account was created for the address you signed in with.',
    tone: 'error',
  },
  ACCOUNT_DEACTIVATED: {
    title: 'Access revoked / contact admin',
    detail:
      'This account does not currently have access. Signing in again will not restore it — please contact a BoB Manager administrator, who can reactivate it from Settings.',
    tone: 'error',
  },
  /*
   * The one code the CLIENT raises rather than the callback: a session that ended mid-use
   * (`onAuthLost` in `api/client.ts`). A notice, not an error — nothing failed, a timeout did
   * what it is for. ⚠ It must not fall through to `UNAUTHENTICATED` below, whose "could not be
   * verified" sends a reader looking for a fault that is not there.
   */
  SESSION_EXPIRED: {
    title: 'Your session has expired',
    detail:
      'For security, BoB Manager signs you out after a period without activity, and once a day regardless. Sign in again to continue.',
    tone: 'notice',
  },
  UNAUTHENTICATED: {
    title: 'Sign-in could not be completed',
    detail: 'The sign-in attempt could not be verified. Please try again.',
    tone: 'error',
  },
};

export function Login() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get('error');
  const message = errorCode ? (MESSAGES[errorCode] ?? MESSAGES.UNAUTHENTICATED) : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: color.appBg,
        padding: 24,
      }}
    >
      <main
        style={{
          width: '100%',
          maxWidth: 420,
          background: color.surface,
          border: `1px solid ${color.border}`,
          borderRadius: radius['6xl'],
          padding: '32px 32px 28px',
          boxShadow: elevation.search,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 24 }}>
          <div
            aria-hidden="true"
            style={{
              width: 40,
              height: 40,
              flex: 'none',
              borderRadius: radius['2xl'],
              background: `linear-gradient(150deg, ${color.brandFrom}, ${color.brandTo})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              color: color.onBrand,
              fontSize: 18,
              boxShadow: elevation.brand,
            }}
          >
            B
          </div>
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: fontSize.cardTitle,
                letterSpacing: '-.2px',
                color: color.ink,
              }}
            >
              BoB Manager
            </div>
            <div
              style={{
                fontSize: fontSize.label,
                color: color.inkSubtle,
                letterSpacing: '.3px',
              }}
            >
              HEALTHCARE DIVISION
            </div>
          </div>
        </div>

        {message ? (
          <div
            role="alert"
            style={{
              background: message.tone === 'notice' ? color.warningBg : color.dangerBg,
              border: `1px solid ${
                message.tone === 'notice' ? color.warningBorder : color.negativeStrong
              }`,
              color: message.tone === 'notice' ? color.noticeInk : color.dangerInk,
              borderRadius: radius['3xl'],
              padding: '12px 14px',
              marginBottom: 20,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: fontSize.bodyStrong }}>
              {message.title}
            </div>
            <div style={{ fontSize: fontSize.body, marginTop: 4 }}>{message.detail}</div>
          </div>
        ) : null}

        <h1
          style={{
            margin: 0,
            fontSize: fontSize.pageTitle,
            fontWeight: 700,
            letterSpacing: '-.3px',
            color: color.ink,
          }}
        >
          Sign in
        </h1>
        <p
          style={{
            margin: '8px 0 24px',
            fontSize: fontSize.bodyStrong,
            color: color.inkMuted,
            lineHeight: 1.5,
          }}
        >
          Use your company Google account. The first time you sign in, an administrator will
          need to approve your access before you can continue.
        </p>

        {/* A full navigation, not a fetch: the OAuth flow is a browser redirect. */}
        <a
          href={api.signInHref()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            width: '100%',
            padding: '12px 16px',
            borderRadius: radius['2xl'],
            background: `linear-gradient(135deg, ${color.signInFrom}, ${color.brandTo})`,
            color: color.onBrand,
            fontSize: fontSize.control,
            fontWeight: 600,
            textDecoration: 'none',
            boxShadow: color.signInGlow,
          }}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            aria-hidden="true"
            fill={color.onBrand}
          >
            <path d="M12 11v2.8h6.6c-.3 1.7-2 5-6.6 5a7 7 0 1 1 0-14c2 0 3.4.9 4.2 1.6l2.1-2A9.8 9.8 0 0 0 12 2a10 10 0 1 0 0 20c5.8 0 9.6-4 9.6-9.8 0-.7 0-1.2-.2-1.7H12Z" />
          </svg>
          Continue with Google
        </a>

        <p
          style={{
            margin: '20px 0 0',
            fontSize: fontSize.caption,
            color: color.inkFaint,
            lineHeight: 1.5,
          }}
        >
          Access is restricted to verified company email addresses and is enforced on the
          server.
        </p>
      </main>
    </div>
  );
}
